import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import ParameterFieldBase, { IParameterFieldBaseOptions } from '../ts/ParameterFieldBase';
import { ParameterField } from './ParameterField';
import StringField from './StringField';
import SelectField from './SelectField';
import NumberField from './NumberField';
import AttributeDesigner, { AttributeDesignerMode } from './attributeDesigner/AttributeDesigner';
import GlobalState from '../ts/globalStates/GlobalState';
import { renderTemplate } from '../../../_common/platform/ts/Utilities';

import { ATTRIBUTE_TYPES } from '../ts/ConfigurationConstants';

import { DataloadEventTypes, IExpression } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';

import { IAttributeName } from '../../../_common/platform/tsx/filtering/Interfaces';

import { IDisplayValue } from '../../../_common/platform/ts/Interfaces';
import { ICultureValues } from '../ts/Globalization';

import BackendMethodParameterOptionsSelect from './BackendMethodParameterOptionsSelect';

import {
    findFirstVariable,
    formatValueByType,
    isValueEmpty,
    sameAttributes,
    getVariableType as getVariableType_FU
} from '../../../_common/platform/tsx/filtering/FilterUtilities';

export type ValueSourceType = 'constant' | 'filter-value' | 'exchange-object';

export interface IValueSource extends IAttributeName {
    constant?: any;
}

export interface IBackendParameter {
    name?: string;
    valueSourceType: ValueSourceType;
    valueSource?: IValueSource;
    valueType?: string;
    requireValue?: boolean;
    requireValueMessage?: string;
    exchangeObjectName?: string;
}

export interface IBackendMethodConfiguratorValue {
    name?: string;
    dataSourceID?: number;
    parameters?: IBackendParameter[];
}

export interface IBackendMethodConfiguratorProperties {
    value?: IBackendMethodConfiguratorValue;
    glob: IGlob;
    options?: BackendMethodConfiguratorOptions;

    onChange?(value: IBackendMethodConfiguratorValue): void;
}

export function getDefaultRequireValueMessage(valueSource: IValueSource) {
    return `Value for '${valueSource.attribute ?? valueSource.expression.text}' must be in Exchange Object.`;
}

export function prepareParameters(
    methodConfig: IBackendMethodConfiguratorValue,
    globalState: GlobalState,
    cultureValues: ICultureValues,
    glob: IGlob
) {
    if (methodConfig == null) {
        return;
    }

    const { parameters, dataSourceID } = methodConfig;
    const aqlParameters: { [name: string]: any } = {};

    let errorMessage: string;

    if (parameters != null) {
        const globalFilterCollection = globalState.getNotAppliedAttributeFilterCollection();
        const allVariables = globalState.getAllVariables(dataSourceID, cultureValues, {}, globalFilterCollection);
        const attributeFilters = globalState.getAttributeFilters(dataSourceID, null, globalFilterCollection, true);
        const bufferedData = globalState.getExchangeObject();

        for (let parameter of parameters) {
            const { valueSource, valueSourceType } = parameter;

            let value: any;

            if (parameter.name != null && valueSource != null) {
                if (valueSourceType === 'constant') {
                    value = valueSource.constant;
                } else {
                    let values: IDisplayValue[];

                    if (valueSourceType === 'filter-value') {
                        if (valueSource.attribute != null && valueSource.attribute.indexOf('@') === 0) {
                            const variable = findFirstVariable(allVariables, valueSource.attribute, true);

                            if (variable != null) {
                                values = variable.values;
                            }
                        } else if (attributeFilters != null) {
                            let attributeFilter = _.find(attributeFilters, (filter) => {
                                return !filter.clickToFilterAttribute && sameAttributes(filter, valueSource);
                            });

                            if (attributeFilter == null || isValueEmpty(attributeFilter.values)) {
                                attributeFilter = _.find(attributeFilters, (filter) => {
                                    return filter.clickToFilterAttribute && sameAttributes(filter, valueSource);
                                });
                            }

                            if (attributeFilter != null && !isValueEmpty(attributeFilter.values)) {
                                values = attributeFilter.values;
                            }
                        }

                        if (values != null) {
                            if (values.length === 1) {
                                value = values[0];
                            } else {
                                value = values.map((val) => {
                                    return val.value;
                                });
                            }
                        }

                        if (value == null && parameter.requireValue) {
                            errorMessage = parameter.requireValueMessage;

                            if (errorMessage == null) {
                                errorMessage = `Value for '${
                                    valueSource.attribute || valueSource.expression.text
                                }' must be specified in Attribute Filter.`;
                            }
                            break;
                        }
                    } else if (valueSourceType === 'exchange-object') {
                        const record = bufferedData.find((record) => {
                            return (
                                sameAttributes(record, valueSource) &&
                                record.exchangeObjectName === parameter.exchangeObjectName
                            );
                        });

                        if (record != null) {
                            value = record.values.map((value) => {
                                //For Data Edit.
                                if (value.id != null && value.val != null) {
                                    return value.id;
                                } else {
                                    return value;
                                }
                            });

                            if (value.length === 1) {
                                value = value[0];
                            }
                        }

                        if (value == null && parameter.requireValue) {
                            errorMessage = parameter.requireValueMessage;

                            if (errorMessage == null) {
                                errorMessage = getDefaultRequireValueMessage(valueSource);
                            }
                            break;
                        }
                    }
                }

                if (value != null) {
                    value = formatValueByType(value, parameter.valueType);

                    if (value == null) {
                        errorMessage = `Type of Value for '${
                            valueSource.attribute || valueSource.expression.text
                        }' is not the same as specified in the configuraion.`;
                        break;
                    }

                    aqlParameters[parameter.name] = value;
                }
            }
        }
    }

    if (errorMessage == null) {
        return aqlParameters;
    } else {
        glob.AppActions.showNotification_Warning(errorMessage);
        return null;
    }
}

export function callBackendMethod(
    methodConfig: IBackendMethodConfiguratorValue,
    globalState: GlobalState,
    cultureValues: ICultureValues,
    glob: IGlob
) {
    const aqlParameters = prepareParameters(methodConfig, globalState, cultureValues, glob);

    if (aqlParameters != null && methodConfig.name != null && methodConfig.name.length > 0) {
        return glob.AQLQueries.callMethod('attrQuery_ActionButton', {
            name: methodConfig.name,
            dataSourceID: methodConfig.dataSourceID,
            parameters: aqlParameters
        });
    } else {
        return null;
    }
}

export function bindJavaScriptCall(
    javaStringCall: string,
    methodConfig: IBackendMethodConfiguratorValue,
    globalState: GlobalState,
    cultureValues: ICultureValues,
    glob: IGlob
): string {
    const bindParameters = prepareParameters(methodConfig, globalState, cultureValues, glob);

    if (bindParameters != null) {
        return renderTemplate(javaStringCall, bindParameters);
    } else {
        return javaStringCall;
    }
}

function normalizeParameter(value: IBackendParameter) {
    switch (value.valueSourceType) {
        case 'constant':
            delete value.requireValue;
            delete value.requireValueMessage;
            delete value.exchangeObjectName;
            break;

        case 'filter-value':
            delete value.exchangeObjectName;
        case 'exchange-object':
            if (value.requireValue === false) {
                delete value.requireValue;
                delete value.requireValueMessage;
            }

        default:
    }
}

function ParameterRow(props: {
    cultureValues: ICultureValues;
    value?: IBackendParameter;
    index?: number;
    dataSourceID?: number;
    glob?: IGlob;
    tabID?: number;

    onAdd?(): void;
    onChange?(value: IBackendMethodConfiguratorValue, index: number): void;
    onRemove?(index: number): void;
}) {
    let removeButton: React.JSX.Element;
    let nameElement: React.JSX.Element;
    let valueSourceTypeElement: React.JSX.Element;
    let valueSourceElement: React.JSX.Element;
    let valueTypeElement: React.JSX.Element;
    let optionsElement: React.JSX.Element;

    if (props.value != null) {
        const { valueSource } = props.value;

        const processOnChange = (value: IBackendParameter) => {
            normalizeParameter(value);
            props.onChange(value, props.index);
        };

        removeButton = (
            <span
                className="glyphicon glyphicon-remove"
                onClick={() => {
                    props.onRemove(props.index);
                }}
            ></span>
        );

        const handleOnNameChange = (value: string) => {
            const valueCopy = { ...props.value };

            valueCopy.name = value;
            processOnChange(valueCopy);
        };

        nameElement = <StringField value={props.value.name} allowNull={false} onBlur={handleOnNameChange} />;

        const handleOnValueSourceTypeChange = (value: string) => {
            const valueCopy = { ...props.value };

            valueCopy.valueSourceType = value as ValueSourceType;
            delete valueCopy.valueSource;

            if (valueCopy.valueSourceType === 'constant') {
                valueCopy.valueType = ATTRIBUTE_TYPES.STRING;
            } else {
                delete valueCopy.valueType;
            }

            processOnChange(valueCopy);
        };

        const { valueSourceType } = props.value;

        valueSourceTypeElement = (
            <SelectField
                value={valueSourceType}
                options={{
                    values: ['constant', 'filter-value', 'exchange-object'],
                    display: ['Constant', 'Attribute Filter', 'Exchange Object']
                }}
                onChange={handleOnValueSourceTypeChange}
            />
        );

        if (valueSourceType === 'constant') {
            const handleOnConstantValueSourceChange = (value) => {
                const valueCopy = { ...props.value };

                valueCopy.valueSource = {
                    constant: value
                };
                processOnChange(valueCopy);
            };

            const value = valueSource != null ? valueSource.constant : null;

            switch (props.value.valueType) {
                case ATTRIBUTE_TYPES.INTEGER:
                    valueSourceElement = (
                        <NumberField
                            value={value}
                            integer={true}
                            allowNull={true}
                            onBlur={handleOnConstantValueSourceChange}
                        />
                    );
                    break;

                default:
                    valueSourceElement = (
                        <StringField value={value} allowNull={true} onBlur={handleOnConstantValueSourceChange} />
                    );
                    break;
            }

            const handleOnValueTypeChange = (value: string) => {
                const valueCopy = { ...props.value };

                valueCopy.valueType = value;
                delete valueCopy.valueSource;
                processOnChange(valueCopy);
            };

            valueTypeElement = (
                <SelectField
                    value={props.value.valueType}
                    options={{
                        values: [ATTRIBUTE_TYPES.STRING, ATTRIBUTE_TYPES.INTEGER],
                        display: ['string', 'integer']
                    }}
                    onChange={handleOnValueTypeChange}
                />
            );
        } else {
            const entitiesTree = props.glob.EntityModelsStore.getEntitiesTreeByDS(props.dataSourceID);

            const handleOnAttributeChange = (values: string[]) => {
                const valueCopy = { ...props.value };

                valueCopy.valueSource = {
                    attribute: values[0]
                };
                valueCopy.valueType = props.glob.EntityModelsStore.getDefaultAttributeType(
                    valueCopy.valueSource,
                    props.dataSourceID
                );
                processOnChange(valueCopy);
            };

            const getVariableType = (variableName: string) => {
                const globalState = GlobalState.getGlobalState(props.glob, props.tabID);

                let result: string;

                if (globalState != null) {
                    const allVariables = globalState.getAllVariables(props.dataSourceID, props.cultureValues, {});
                    const variable = findFirstVariable(allVariables, variableName, false);

                    if (variable != null) {
                        result = getVariableType_FU(
                            variable.controlType,
                            variable.attribute,
                            variable.options,
                            props.dataSourceID,
                            props.glob
                        );
                    }
                }

                return result;
            };

            const handleOnVariableNameChange = (value: string) => {
                const valueCopy = { ...props.value };

                if (value.indexOf('@') !== 0) {
                    value = '@' + value;
                }

                valueCopy.valueSource = {
                    attribute: value
                };

                valueCopy.valueType = getVariableType(value);

                if (valueCopy.valueType == null) {
                    valueCopy.valueType = ATTRIBUTE_TYPES.INTEGER;
                }

                processOnChange(valueCopy);
            };

            const handleOnExpressionChange = (value: IExpression) => {
                if (value == null) {
                    return;
                }

                const valueCopy = { ...props.value };

                valueCopy.valueSource = {
                    expression: value
                };
                valueCopy.valueType = value.rettype;

                processOnChange(valueCopy);
            };

            let value: string | IExpression;
            let currentMode: AttributeDesignerMode = 'attribute';

            if (valueSource != null) {
                if (valueSource.attribute != null) {
                    value = valueSource.attribute;

                    if (value.indexOf('@') === 0) {
                        value = value.slice(1, value.length);
                        currentMode = 'variable';
                    }
                } else if (valueSource.expression != null) {
                    value = valueSource.expression;
                    currentMode = 'expression';
                }
            }

            valueSourceElement = (
                <AttributeDesigner
                    tree={entitiesTree}
                    dataSourceID={props.dataSourceID}
                    modes={['attribute', 'expression', 'variable']}
                    value={value}
                    currentMode={currentMode}
                    showRemoveButton={false}
                    doNotAddSpecialEntities={true}
                    multiselect={false}
                    glob={props.glob}
                    tabID={props.tabID}
                    onChange={handleOnAttributeChange}
                    onVariableNameChange={handleOnVariableNameChange}
                    onExpressionChange={handleOnExpressionChange}
                />
            );

            const attribute = props.value.valueSource?.attribute;

            let variableTypeIsNotDefined = false;

            if (attribute != null && attribute.indexOf('@') === 0) {
                variableTypeIsNotDefined = getVariableType(attribute) == null;
            }

            if (variableTypeIsNotDefined) {
                const handleOnValueTypeChange = (value: string) => {
                    const valueCopy = { ...props.value };

                    valueCopy.valueType = value;
                    processOnChange(valueCopy);
                };

                valueTypeElement = (
                    <SelectField
                        value={props.value.valueType}
                        options={{
                            values: [ATTRIBUTE_TYPES.STRING, ATTRIBUTE_TYPES.INTEGER],
                            display: ['string', 'integer']
                        }}
                        onChange={handleOnValueTypeChange}
                    />
                );
            } else {
                let valueTypeToShow: string;

                switch (props.value.valueType) {
                    case ATTRIBUTE_TYPES.INTEGER:
                        valueTypeToShow = 'integer';
                        break;

                    case ATTRIBUTE_TYPES.STRING:
                        valueTypeToShow = 'string';
                        break;

                    default:
                        valueTypeToShow = props.value.valueType;
                }

                valueTypeElement = <span>{valueTypeToShow}</span>;
            }

            const handleOnOptionsChange = (value: IBackendParameter) => {
                processOnChange({ ...value });
            };

            optionsElement = (
                <BackendMethodParameterOptionsSelect
                    value={props.value}
                    glob={props.glob}
                    onChange={handleOnOptionsChange}
                />
            );
        }
    } else {
        nameElement = (
            <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={() => {
                    props.onAdd();
                }}
            >
                {'Add'}
            </button>
        );
    }

    return (
        <tr>
            <td className="cell-remove border-0">{removeButton}</td>
            <td className="cell-parameter-name">{nameElement}</td>
            <td className="cell-parameter-value-source-type">{valueSourceTypeElement}</td>
            <td className="cell-parameter-value-source">{valueSourceElement}</td>
            <td className="cell-parameter-value-type">{valueTypeElement}</td>
            {/* <td className="cell-parameter-require-value">{ requireValueElement }</td> */}
            <td className="cell-parameter-options">{optionsElement}</td>
        </tr>
    );
}

function BackendMethodConfigurator(props: IBackendMethodConfiguratorProperties) {
    const [dataSourceID, setDataSourceID] = React.useState(() => {
        let result = props.value != null ? props.value.dataSourceID : null;

        if (result == null) {
            result = props.glob.EntityModelsStore.getStoredDataSourceID();
        }

        return result;
    });
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        const handleOnEntityModelsChange = (eventType: DataloadEventTypes) => {
            if (eventType === DataloadEventTypes.MODEL) {
                setLoading(false);
            }
        };

        props.glob.EntityModelsStore.loadDataSources();
        props.glob.EntityModelsStore.addOnDataLoadListener(handleOnEntityModelsChange);

        return () => {
            props.glob.EntityModelsStore.removeOnDataLoadListener(handleOnEntityModelsChange);
        };
    }, []);

    React.useEffect(() => {
        setLoading(true);
        props.glob.EntityModelsStore.loadModels([dataSourceID]);
    }, [dataSourceID]);

    const handleOnMethodNameChange = (value: string) => {
        if (props.onChange != null) {
            const valueCopy = { ...props.value };

            valueCopy.name = value;

            props.onChange(valueCopy);
        }
    };

    const handleOnDataSourceChange = (value: string) => {
        if (props.onChange != null) {
            const valueCopy = { ...props.value };
            const newDataSourceID = parseInt(value, 10);

            valueCopy.dataSourceID = newDataSourceID;
            props.glob.EntityModelsStore.storeDataSourceID(newDataSourceID);

            if (valueCopy.parameters != null) {
                valueCopy.parameters.forEach((parameter) => {
                    if (parameter.valueSourceType === 'filter-value') {
                        delete parameter.valueSource;
                        delete parameter.valueType;
                    }
                });
            }

            setDataSourceID(newDataSourceID);
            props.onChange(valueCopy);
        }
    };

    const handleOnParameterAdd = () => {
        if (props.onChange != null) {
            const valueCopy = { ...props.value };

            if (valueCopy.dataSourceID == null) {
                valueCopy.dataSourceID = dataSourceID;
            }

            let parameters = valueCopy.parameters;

            if (parameters == null) {
                parameters = valueCopy.parameters = [];
            }

            parameters.push({
                valueSourceType: 'filter-value'
            });

            props.onChange(valueCopy);
        }
    };

    const handleOnParameterChange = (value: IBackendParameter, index: number) => {
        if (props.onChange != null) {
            const valueCopy = { ...props.value };

            valueCopy.parameters[index] = value;
            props.onChange(valueCopy);
        }
    };

    const handleOnParameterRemove = (index: number) => {
        if (props.onChange != null) {
            const valueCopy = { ...props.value };

            valueCopy.parameters.splice(index, 1);
            props.onChange(valueCopy);
        }
    };

    const { options } = props;
    const dataSources = props.glob.EntityModelsStore.getDataSources();
    const dataSourceOptions = {
        values: [],
        display: []
    };

    _.each(dataSources, (datasource) => {
        dataSourceOptions.values.push(datasource.dataSourceID.toString());
        dataSourceOptions.display.push(datasource.name);
    });

    let parameterRows: React.JSX.Element[];

    if (props.value != null && props.value.parameters != null) {
        parameterRows = props.value.parameters.map((parameter, index) => (
            <ParameterRow
                key={parameter.name + '_' + index}
                value={parameter}
                index={index}
                dataSourceID={dataSourceID}
                cultureValues={options?.cultureValues}
                glob={props.glob}
                tabID={options?.tabID}
                onChange={handleOnParameterChange}
                onRemove={handleOnParameterRemove}
            />
        ));
    }

    return (
        <div className="form-group parameter-field backend-method-configurator">
            {options?.hideMethodName ? null : (
                <ParameterField noTitle={true}>
                    <StringField
                        value={props.value != null ? props.value.name : ''}
                        onBlur={handleOnMethodNameChange}
                    />
                </ParameterField>
            )}

            <ParameterField title={'Data Source'}>
                <SelectField
                    value={dataSourceID != null ? dataSourceID.toString() : null}
                    options={dataSourceOptions}
                    onChange={handleOnDataSourceChange}
                />
            </ParameterField>

            <table className={'table backend-parameters'}>
                <thead>
                    <tr>
                        <th className="cell-remove border-0"></th>
                        <th className="cell-parameter-name">{'Parameter Name'}</th>
                        <th className="cell-parameter-value-source-type">{'Take Value From'}</th>
                        <th className="cell-parameter-value-source">{'Value Source'}</th>
                        <th className="cell-parameter-value-type">{'Value Type'}</th>
                        {/* <th className="cell-parameter-require-value">{ 'Require Value' }</th> */}
                        <th className="cell-parameter-options">{'More...'}</th>
                    </tr>
                </thead>
                <tbody>
                    {parameterRows}

                    <ParameterRow cultureValues={options?.cultureValues} onAdd={handleOnParameterAdd} />
                </tbody>
            </table>
        </div>
    );
}

interface BackendMethodConfiguratorOptions extends IParameterFieldBaseOptions {
    hideMethodName?: boolean;
    cultureValues?: ICultureValues;
}

function validateBackendMethodConfiguratorValue(value: IBackendMethodConfiguratorValue) {
    if (this.options?.hideMethodName === true) {
        return null;
    }

    if (value != null) {
        if (value.name == null || value.name.length === 0) {
            return ['Method Name is not specified'];
        }
    }

    return null;
}

export class BackendMethodConfiguratorField extends ParameterFieldBase<BackendMethodConfiguratorOptions> {
    public static defaultValidators = [validateBackendMethodConfiguratorValue];
    public static EditComponent = BackendMethodConfigurator;
    public getDefaultOptions() {
        return {};
    }

    constructor(glob: IGlob, options?: BackendMethodConfiguratorOptions) {
        const title = options?.hideMethodName === true ? '' : 'Method Name';

        super(glob, title, BackendMethodConfiguratorField.defaultValidators, [], null, options);
    }
}
